import React, {useCallback, useContext, useState} from 'react';
import Form, {FormContext} from "../Form";
import UserEvent from "../../core/UserEvent";
import {Divider} from "@material-ui/core";
import Box from "../Box";
import PropTypes from "prop-types";
import lang from "../../core/lang";
import clsx from "clsx";

const initialContext = {
    value: () => ([]),
    add: () => {},
    remove: () => {},
    up: () => {},
    down: () => {},
    readOnly: () => false,
    disabled: () => false
}

const FormsContext = React.createContext(initialContext);

function Forms(props) {
    const {pageNumber, pageSize, children, divider, readOnly, disabled} = props;
    const value = props.value ? [...props.value] : [];
    const style = {...props.style};

    const handleChange = index => event => {
        value[index] = event.value;
        let change = event.change
        let action;
        //若value長度為一，且那一筆資料的值都是null或undefined，則value設為空陣列，且action為remove，change為刪除的那筆資料
        if (value.length === 1 && Object.values(value[0]).every(value => lang.isEmpty(value))) {
            change = props.value[0];
            value.splice(0, 1);
            //此情況下的change的結果和user去按刪除按鈕是一樣的，所以action應該要是remove
            action = 'remove';
        }
        props.onChange && props.onChange(new UserEvent({
            value: value,
            change,
            index,
            action
        }));
    }

    if(!readOnly && value.length===0) {
        value.push({});
    }

    const offset = pageNumber * pageSize;
    const size = pageSize || value.length;
    const end = offset + size;
    const elements = [];
    for(let index=offset; index<end; index++) {
        const item = value[index];
        elements.push(
            <Form key={index*2} value={item} index={index} onChange={handleChange(index)} children={children}
                  readOnly={readOnly} disabled={disabled} />
        );
        divider && (index+1<end) && elements.push(<Divider key={index*2+1} variant="fullWidth" />)
    }

    const options = lang.omit(props, 'value', 'onChange', 'children', 'pageNumber', 'pageSize', 'divider', 'readOnly', 'disabled');
    options.className = clsx('appfuse-forms', options.className);

    const context = {
        value: () => [...value],
        add: (item, index) => {
            value.splice(index+1, 0, item);
            props.onChange && props.onChange(new UserEvent({
                value: value,
                change: item,
                index,
                action: 'add',
            }));
        },
        remove: index => {
            const [item] = value.splice(index, 1);
            //若value長度為一，且那一筆資料的值都是null或undefined，則value設為空陣列
            if (value.length === 1 && Object.values(value[0]).every(value => lang.isEmpty(value))) {
                value.splice(0, 1);
            }
            props.onChange && props.onChange(new UserEvent({
                value: value,
                change: item,
                index,
                action: 'remove'
            }));
        },
        up: index => {
            if (index > 0 && index < value.length) {
                const item = value[index];
                value.splice(index, 1); // Remove the item from the current position
                value.splice(index - 1, 0, item); // Insert the item one position up
            }
            props.onChange && props.onChange(new UserEvent({
                value: value,
                index,
                action: 'up'
            }));
        },
        down: index => {
            if (index >= 0 && index < value.length - 1) {
                const item = value[index];
                value.splice(index, 1); // Remove the item from the current position
                value.splice(index + 1, 0, item); // Insert the item one position down
            }
            props.onChange && props.onChange(new UserEvent({
                value: value,
                index,
                action: 'down'
            }));
        },
        readOnly: () => readOnly,
        disabled: () => disabled
    }

    if(readOnly) {
        style.pointerEvents = '';
    }

    return (
        <FormsContext.Provider value={context}>
            <Box {...options} style={style}>{elements}</Box>
        </FormsContext.Provider>
    );
}

function AddAction(props) {
    const form = useContext(FormContext);
    const forms = useContext(FormsContext);
    const [disabled, setDisabled] = useState(false);

    const handleClick = () => {
        setDisabled(true);
        forms.add({}, form.index());
    };

    lang.delay(()=>setDisabled(false), 500);

    return (
        <Box {...props} onClick={handleClick} disabled={props.disabled || disabled || forms.readOnly() || forms.disabled()} />
    )
}

function RemoveAction(props) {
    const form = useContext(FormContext);
    const forms = useContext(FormsContext);

    const handleClick = () => {
        forms.remove(form.index());
    };


    return (
        <Box {...props} onClick={handleClick} disabled={props.disabled || forms.readOnly() || forms.disabled()} />
    )
}

function UpAction(props) {
    const form = useContext(FormContext);
    const forms = useContext(FormsContext);

    const handleClick = () => {
        forms.up(form.index());
    };


    return (
        <Box {...props} onClick={handleClick} disabled={props.disabled || forms.readOnly() || forms.disabled()}/>
    )
}

function DownAction(props) {
    const form = useContext(FormContext);
    const forms = useContext(FormsContext);

    const handleClick = () => {
        forms.down(form.index());
    };

    return (
        <Box {...props} onClick={handleClick} disabled={props.disabled || forms.readOnly() || forms.disabled()}/>
    )
}

Forms.propTypes = {
    value: PropTypes.arrayOf(PropTypes.object),
    pageNumber: PropTypes.number,
    pageSize: PropTypes.number,
    divider: PropTypes.bool,
    readOnly: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),

    // props inherits from Box
    // Display
    display: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.string
    ]),
    displayPrint: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.string
    ]),
    overflow: PropTypes.string,
    textOverflow: PropTypes.string,
    visibility: PropTypes.string,
    cursor: PropTypes.string,

    // Shadows
    boxShadow: PropTypes.number,

    // Flexbox
    flexDirection: PropTypes.string,
    flexWrap: PropTypes.string,
    justifyContent: PropTypes.string,
    alignItems: PropTypes.string,
    alignContent: PropTypes.string,
    gap: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    order: PropTypes.number,
    flexGrow: PropTypes.number,
    flexShrink: PropTypes.number,
    alignSelf: PropTypes.string,

    // Spacing
    padding: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    paddingX: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    paddingY: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    paddingTop: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    paddingRight: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    paddingBottom: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    paddingLeft: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    margin: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    marginX: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    marginY: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    marginTop: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    marginRight: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    marginBottom: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    marginLeft: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),

    // Sizing
    width: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    maxWidth: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    minWidth: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    height: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    maxHeight: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    minHeight: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    boxSizing: PropTypes.string,

    // Typography
    fontFamily: PropTypes.string,
    fontSize: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    fontStyle: PropTypes.string,
    fontWeight: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    letterSpacing: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    lineHeight: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    whiteSpace: PropTypes.string,

    // Palette
    color: PropTypes.string,
    backgroundColor: PropTypes.string,

    // Border
    border: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    borderTop: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    borderLeft: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    borderRight: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    borderBottom: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    borderRadius: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    borderColor: PropTypes.string,

    // Position
    position: PropTypes.string,
    zIndex: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    top: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    right: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    bottom: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    left: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),

    // Event
    onChange: PropTypes.func,
    onClick: PropTypes.func,
    disabled: PropTypes.bool
}

Forms.defaultProps = {
    pageNumber: 0,
    pageSize: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
    divider: true
}

export default Forms;
export {FormsContext, AddAction, RemoveAction, UpAction, DownAction};